import { MAP_LAYER_ORDER } from './constants';
import { resolveHexaFill } from './color';
import { MapSceneError } from './errors';
import { clientPointToMapPoint } from './geometry/client-point-to-map-point';
import {
  createCameraLabelElement,
  renderCameraLabelsLayer,
} from './layers/render-camera-labels-layer';
import { createCameraElement, renderCamerasLayer } from './layers/render-cameras-layer';
import {
  createFieldLabelElement,
  renderFieldLabelsLayer,
} from './layers/render-field-labels-layer';
import { createFieldElement, renderFieldsLayer } from './layers/render-fields-layer';
import { renderStreetsLayer } from './layers/render-streets-layer';
import { createSvgElement } from './svg';
import type {
  ClientPoint,
  CreateMapSceneParams,
  MapEntityReference,
  MapLayerId,
  MapPoint,
  MapRenderCameraModel,
  MapRenderFieldModel,
  MapRenderModel,
  MapScene,
} from './types';

/**
 * Resolves a deterministic unique layer set from an arbitrary caller-supplied subset.
 *
 * @param visibleLayers Requested renderer layer identifiers in any order.
 * @returns A new deduplicated layer list in canonical SVG stacking order.
 * @throws {MapSceneError} When an identifier is unknown or repeated.
 */
export function resolveVisibleLayers(visibleLayers: readonly MapLayerId[]): readonly MapLayerId[] {
  const seenLayers = new Set<MapLayerId>();

  for (const visibleLayer of visibleLayers) {
    if (!MAP_LAYER_ORDER.includes(visibleLayer)) {
      throw new MapSceneError({
        code: 'INVALID_CONFIGURATION',
        message: `Unknown map layer: ${String(visibleLayer)}.`,
      });
    }

    if (seenLayers.has(visibleLayer)) {
      throw new MapSceneError({
        code: 'INVALID_CONFIGURATION',
        message: `Map layer is configured more than once: ${visibleLayer}.`,
      });
    }

    seenLayers.add(visibleLayer);
  }

  return MAP_LAYER_ORDER.filter((layerId) => seenLayers.has(layerId));
}

/**
 * Replaces a field by canonical identifier without mutating the source collection.
 *
 * @param params Existing immutable fields and a replacement using one known canonical field ID.
 * @returns New field collection with exactly one replacement.
 * @throws {MapSceneError} When the requested field is absent.
 */
export function replaceFieldModel(params: {
  fields: readonly MapRenderFieldModel[];
  field: MapRenderFieldModel;
}): readonly MapRenderFieldModel[] {
  const fieldIndex = params.fields.findIndex((field) => field.id === params.field.id);
  if (fieldIndex === -1) {
    throw new MapSceneError({
      code: 'ENTITY_NOT_FOUND',
      message: `Map field was not found: ${params.field.id}.`,
    });
  }

  return params.fields.map((field, index) => (index === fieldIndex ? params.field : field));
}

/**
 * Replaces a camera by canonical map-position identifier without mutating the source collection.
 *
 * @param params Existing immutable camera placements and a replacement for one known placement ID.
 * @returns New camera collection with exactly one replacement.
 * @throws {MapSceneError} When the requested camera placement is absent.
 */
export function replaceCameraModel(params: {
  cameras: readonly MapRenderCameraModel[];
  camera: MapRenderCameraModel;
}): readonly MapRenderCameraModel[] {
  const cameraIndex = params.cameras.findIndex(
    (camera) => camera.mapPositionId === params.camera.mapPositionId,
  );
  if (cameraIndex === -1) {
    throw new MapSceneError({
      code: 'ENTITY_NOT_FOUND',
      message: `Map camera was not found: ${params.camera.mapPositionId}.`,
    });
  }

  return params.cameras.map((camera, index) => (index === cameraIndex ? params.camera : camera));
}

/**
 * Creates a detached-map SVG lifecycle facade without mounting or rendering it eagerly.
 *
 * The facade owns only SVG nodes it creates. Page code remains responsible for fetching,
 * interaction listeners, tooltips, dialogs, legends, loading state, and error presentation.
 *
 * @param params Page-owned mount point, visual model, layer subset and accessibility metadata.
 * @returns Detached scene lifecycle facade; call `render()` to mount its SVG root.
 * @throws {MapSceneError} When configuration or visual data violates the shared renderer contract.
 */
export function createMapScene(params: CreateMapSceneParams): MapScene {
  validateSceneConfiguration(params);
  let model = params.model;
  validateRenderModel(model);
  const visibleLayers = resolveVisibleLayers(params.visibleLayers);
  let svgElement: SVGSVGElement | null = null;
  let destroyed = false;

  /**
   * Guards every lifecycle operation against use after `destroy()`.
   *
   * @returns Nothing.
   * @throws {MapSceneError} When this scene lifecycle is already closed.
   */
  const assertActive = (): void => {
    if (destroyed) {
      throw new MapSceneError({
        code: 'DESTROYED',
        message: 'The map scene has already been destroyed.',
      });
    }
  };

  /**
   * Obtains the current SVG root after verifying the scene lifecycle and mount state.
   *
   * @returns Current renderer-owned SVG root.
   * @throws {MapSceneError} When the scene was destroyed or has not been rendered yet.
   */
  const assertRendered = (): SVGSVGElement => {
    assertActive();
    if (svgElement === null) {
      throw new MapSceneError({
        code: 'NOT_RENDERED',
        message: 'The map scene must be rendered before this operation.',
      });
    }

    return svgElement;
  };

  /**
   * Builds a fresh detached SVG tree from the current model and selected layers.
   *
   * @returns New detached SVG root with renderer-owned semantic hooks.
   * @throws {MapSceneError} When layer construction fails or a lower-level failure is wrapped.
   */
  const buildSvg = (): SVGSVGElement => {
    try {
      const nextSvgElement = createSvgElement('svg');
      nextSvgElement.dataset.mapScene = 'true';
      nextSvgElement.setAttribute('width', String(model.canvas.width));
      nextSvgElement.setAttribute('height', String(model.canvas.height));
      nextSvgElement.setAttribute('viewBox', `0 0 ${model.canvas.width} ${model.canvas.height}`);
      nextSvgElement.setAttribute('role', params.accessibility.role);
      nextSvgElement.setAttribute('aria-label', params.accessibility.label);
      nextSvgElement.classList.add(...(params.rootClassNames ?? []));

      for (const layerId of visibleLayers) {
        nextSvgElement.append(createLayer({ layerId, model }));
      }

      return nextSvgElement;
    } catch (cause) {
      if (cause instanceof MapSceneError) {
        throw cause;
      }

      throw new MapSceneError({
        code: 'RENDER_FAILED',
        message: 'The SVG map scene could not be rendered.',
        cause,
      });
    }
  };

  /**
   * Mounts a fresh SVG root or replaces the previous renderer-owned root.
   *
   * @returns Newly mounted SVG root.
   * @throws {MapSceneError} When the scene is closed or SVG construction fails.
   */
  const render = (): SVGSVGElement => {
    assertActive();
    const nextSvgElement = buildSvg();
    const previousSvgElement = svgElement;

    if (previousSvgElement !== null && previousSvgElement.isConnected) {
      previousSvgElement.replaceWith(nextSvgElement);
    } else {
      params.mountElement.append(nextSvgElement);
    }

    svgElement = nextSvgElement;
    return nextSvgElement;
  };

  /**
   * Replaces the full visual model transactionally and rerenders an already mounted scene.
   *
   * @param nextModel New immutable visual model supplied by the caller.
   * @returns Nothing. Restores the previous model if rendering the replacement fails.
   * @throws {MapSceneError} When the new model is invalid, the scene is closed, or rendering fails.
   */
  const replaceData = (nextModel: MapRenderModel): void => {
    assertActive();
    validateRenderModel(nextModel);
    const previousModel = model;
    model = nextModel;
    try {
      if (svgElement !== null) {
        render();
      }
    } catch (error) {
      model = previousModel;
      throw error;
    }
  };

  /**
   * Applies an atomic polygon-and-label update for one existing field.
   *
   * @param field Replacement visual data identified by a canonical field ID.
   * @returns Nothing.
   * @throws {MapSceneError} When the scene, entity or enabled layers cannot satisfy the update.
   */
  const updateField = (field: MapRenderFieldModel): void => {
    const currentSvgElement = assertRendered();
    const nextFields = replaceFieldModel({ fields: model.fields, field });
    validateRenderModel({ ...model, fields: nextFields });
    const theme = model.fieldStatusTheme[field.status];
    const replacements = [
      {
        layerId: 'fields' as const,
        entityKind: 'field' as const,
        replacement: createFieldElement({ field, theme }),
      },
      {
        layerId: 'field-labels' as const,
        entityKind: 'field-label' as const,
        replacement: createFieldLabelElement({ field, theme }),
      },
    ];
    replaceLayerEntities({
      svgElement: currentSvgElement,
      entityId: field.id,
      replacements,
    });
    model = { ...model, fields: nextFields };
  };

  /**
   * Applies an atomic icon-and-label update for one existing camera placement.
   *
   * @param camera Replacement visual data identified by a canonical map-position ID.
   * @returns Nothing.
   * @throws {MapSceneError} When the scene, entity or enabled layers cannot satisfy the update.
   */
  const updateCamera = (camera: MapRenderCameraModel): void => {
    const currentSvgElement = assertRendered();
    const nextCameras = replaceCameraModel({ cameras: model.cameras, camera });
    validateRenderModel({ ...model, cameras: nextCameras });
    replaceLayerEntities({
      svgElement: currentSvgElement,
      entityId: camera.mapPositionId,
      replacements: [
        {
          layerId: 'cameras',
          entityKind: 'camera',
          replacement: createCameraElement(camera),
        },
        {
          layerId: 'camera-labels',
          entityKind: 'camera-label',
          replacement: createCameraLabelElement(camera),
        },
      ],
    });
    model = { ...model, cameras: nextCameras };
  };

  /**
   * Resolves a renderer-owned semantic entity from a page-level event target.
   *
   * @param target Browser event target supplied by the presentation feature.
   * @returns Entity reference, or `null` if the target is outside this scene or has no entity hook.
   * @throws {MapSceneError} When the scene is closed or has not been rendered.
   */
  const getEntityFromTarget = (target: EventTarget | null): MapEntityReference | null => {
    const currentSvgElement = assertRendered();
    if (!(target instanceof Element) || !currentSvgElement.contains(target)) {
      return null;
    }

    let currentElement: Element | null = target;

    while (currentElement !== null && currentElement !== currentSvgElement) {
      const entityReference = resolveEntityReference(currentElement);
      if (entityReference !== null) {
        return entityReference;
      }

      currentElement = currentElement.parentElement;
    }

    return null;
  };

  /**
   * Removes the renderer-owned SVG root and permanently closes the scene lifecycle.
   *
   * @returns Nothing. Repeated calls are intentionally idempotent.
   */
  const destroy = (): void => {
    if (destroyed) {
      return;
    }

    svgElement?.remove();
    svgElement = null;
    destroyed = true;
  };

  return {
    render,
    replaceData,
    updateField,
    updateCamera,
    clientPointToMapPoint: (point: ClientPoint): MapPoint =>
      clientPointToMapPoint({ svgElement: assertRendered(), point }),
    getEntityFromTarget,
    destroy,
  };
}

/**
 * Builds exactly one enabled layer in deterministic scene order.
 *
 * @param params Layer identity and current validated visual model.
 * @returns Detached SVG group for the requested visual layer.
 */
function createLayer(params: { layerId: MapLayerId; model: MapRenderModel }): SVGGElement {
  switch (params.layerId) {
    case 'fields':
      return renderFieldsLayer({
        fields: params.model.fields,
        fieldStatusTheme: params.model.fieldStatusTheme,
      });
    case 'field-labels':
      return renderFieldLabelsLayer({
        fields: params.model.fields,
        fieldStatusTheme: params.model.fieldStatusTheme,
      });
    case 'streets':
      return renderStreetsLayer({
        streets: params.model.streets,
        textColor: params.model.streetTheme.textColor,
      });
    case 'cameras':
      return renderCamerasLayer(params.model.cameras);
    case 'camera-labels':
      return renderCameraLabelsLayer(params.model.cameras);
  }
}

/**
 * Prevalidates and then replaces linked entities for one atomic partial scene update.
 *
 * @param params Current SVG root, canonical entity ID and linked layer replacements to apply.
 * @returns Nothing. No DOM mutation occurs until every enabled target is found.
 * @throws {MapSceneError} When an enabled layer lacks the entity required by a partial update.
 */
function replaceLayerEntities(params: {
  svgElement: SVGSVGElement;
  entityId: number;
  replacements: readonly {
    readonly layerId: MapLayerId;
    readonly entityKind: MapEntityReference['kind'];
    readonly replacement: SVGElement;
  }[];
}): void {
  const entityReplacements: {
    currentEntity: Element;
    replacement: SVGElement;
  }[] = [];

  for (const replacement of params.replacements) {
    const layerElement = findLayerElement(params.svgElement, replacement.layerId);
    if (layerElement === null) {
      continue;
    }

    const currentEntity = Array.from(layerElement.children).find(
      (element) =>
        element instanceof SVGElement &&
        element.dataset.mapEntityKind === replacement.entityKind &&
        element.dataset.mapEntityId === String(params.entityId),
    );

    if (currentEntity === undefined) {
      throw new MapSceneError({
        code: 'ENTITY_NOT_FOUND',
        message: `Rendered map entity was not found: ${replacement.entityKind} ${params.entityId}.`,
      });
    }

    entityReplacements.push({ currentEntity, replacement: replacement.replacement });
  }

  for (const entityReplacement of entityReplacements) {
    entityReplacement.currentEntity.replaceWith(entityReplacement.replacement);
  }
}

/**
 * Finds one renderer-owned layer by its stable semantic hook.
 *
 * @param svgElement Current renderer-owned SVG root.
 * @param layerId Requested semantic layer identifier.
 * @returns Matching SVG group, or `null` when the page did not enable that layer.
 */
function findLayerElement(svgElement: SVGSVGElement, layerId: MapLayerId): SVGGElement | null {
  return (
    Array.from(svgElement.children).find(
      (element): element is SVGGElement =>
        element instanceof SVGGElement && element.dataset.mapLayer === layerId,
    ) ?? null
  );
}

/**
 * Converts a semantic entity DOM hook into its stable public reference.
 *
 * @param element Renderer-owned SVG element which may carry entity data attributes.
 * @returns Semantic entity reference, or `null` when mandatory hooks are malformed or missing.
 */
function resolveEntityReference(element: Element): MapEntityReference | null {
  const kind = element.getAttribute('data-map-entity-kind');
  const id = Number(element.getAttribute('data-map-entity-id'));
  if (
    (kind !== 'field' &&
      kind !== 'field-label' &&
      kind !== 'street' &&
      kind !== 'camera' &&
      kind !== 'camera-label') ||
    !Number.isInteger(id)
  ) {
    return null;
  }

  const cameraId = Number(element.getAttribute('data-map-camera-id'));
  return Number.isInteger(cameraId) ? { kind, id, cameraId } : { kind, id };
}

/**
 * Validates page-supplied scene configuration that TypeScript cannot prove at runtime.
 *
 * @param params Scene creation arguments supplied by a presentation feature.
 * @returns Nothing.
 * @throws {MapSceneError} When the mount, accessibility metadata or CSS class list is invalid.
 */
function validateSceneConfiguration(params: CreateMapSceneParams): void {
  if (!(params.mountElement instanceof HTMLElement)) {
    throw new MapSceneError({
      code: 'INVALID_CONFIGURATION',
      message: 'A valid HTML mount element is required.',
    });
  }

  if (params.accessibility.role !== 'img' || params.accessibility.label.trim() === '') {
    throw new MapSceneError({
      code: 'INVALID_CONFIGURATION',
      message: 'A non-empty image accessibility label is required.',
    });
  }

  if (params.rootClassNames?.some((className) => className.trim() === '')) {
    throw new MapSceneError({
      code: 'INVALID_CONFIGURATION',
      message: 'Root class names must not be empty.',
    });
  }
}

/**
 * Validates the immutable render contract before any SVG nodes are built or replaced.
 *
 * @param model Candidate page-adapted visual model.
 * @returns Nothing.
 * @throws {MapSceneError} When dimensions, entity IDs, geometry, theme or camera placement is invalid.
 */
export function validateRenderModel(model: MapRenderModel): void {
  if (!isPositiveFinite(model.canvas.width) || !isPositiveFinite(model.canvas.height)) {
    throw new MapSceneError({
      code: 'INVALID_MODEL',
      message: 'Map canvas dimensions must be positive finite numbers.',
    });
  }

  validateUniqueCanonicalIds({
    entities: model.fields,
    entityName: 'field',
    resolveId: (field) => field.id,
  });
  validateUniqueCanonicalIds({
    entities: model.streets,
    entityName: 'street',
    resolveId: (street) => street.id,
  });
  validateUniqueCanonicalIds({
    entities: model.cameras,
    entityName: 'camera',
    resolveId: (camera) => camera.mapPositionId,
  });

  for (const field of model.fields) {
    if (
      !Number.isInteger(field.id) ||
      field.points.length < 3 ||
      model.fieldStatusTheme[field.status] === undefined
    ) {
      throw new MapSceneError({
        code: 'INVALID_MODEL',
        message: `Map field has invalid visual data: ${field.id}.`,
      });
    }

    if (!field.points.every((point) => Number.isFinite(point.x) && Number.isFinite(point.y))) {
      throw new MapSceneError({
        code: 'INVALID_MODEL',
        message: `Map field has invalid polygon coordinates: ${field.id}.`,
      });
    }

    try {
      resolveHexaFill(model.fieldStatusTheme[field.status].fillColor);
    } catch (cause) {
      throw new MapSceneError({
        code: 'INVALID_MODEL',
        message: `Map field theme has an invalid fill color: ${field.status}.`,
        cause,
      });
    }
  }

  for (const camera of model.cameras) {
    if (
      !Number.isInteger(camera.mapPositionId) ||
      !Number.isInteger(camera.cameraId) ||
      !Number.isFinite(camera.positionX) ||
      !Number.isFinite(camera.positionY) ||
      !Number.isFinite(camera.rotationDegrees) ||
      !isPositiveFinite(camera.icon.sizeMultiplier)
    ) {
      throw new MapSceneError({
        code: 'INVALID_MODEL',
        message: `Map camera has invalid visual data: ${camera.mapPositionId}.`,
      });
    }
  }
}

/**
 * Validates a canonical entity-identifier set before it can reach semantic hooks or partial updates.
 *
 * @typeParam Entity Visual entity type being checked.
 * @param params Collection, diagnostic name and canonical ID selector.
 * @returns Nothing.
 * @throws {MapSceneError} When an identifier is not an integer or occurs more than once.
 */
function validateUniqueCanonicalIds<Entity>(params: {
  entities: readonly Entity[];
  entityName: string;
  resolveId: (entity: Entity) => number;
}): void {
  const canonicalIds = new Set<number>();

  for (const entity of params.entities) {
    const canonicalId = params.resolveId(entity);
    if (!Number.isInteger(canonicalId) || canonicalIds.has(canonicalId)) {
      throw new MapSceneError({
        code: 'INVALID_MODEL',
        message: `Map ${params.entityName} has a duplicate or invalid canonical ID: ${canonicalId}.`,
      });
    }

    canonicalIds.add(canonicalId);
  }
}

/**
 * Reports whether a number can safely represent a non-zero render dimension or scale.
 *
 * @param value Candidate number received from the page render model.
 * @returns `true` only for finite values greater than zero.
 */
function isPositiveFinite(value: number): boolean {
  return Number.isFinite(value) && value > 0;
}
